import { useDismissHeaderOverlays } from '../lib/HeaderOverlayContext';
import { workspacePickerSearch } from '../lib/useUrlState';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBootstrap } from '../lib/useBootstrap';
import {
  WORKSPACES,
  isWorkspaceId,
  useWorkspace,
  type WorkspaceOption,
  type WorkspaceStatus,
} from '../lib/WorkspaceContext';
import { workspacePath } from '../lib/workspaceRoutes';

function statusLabel(status: WorkspaceStatus): string {
  if (status === 'pilot') return 'Pilot';
  if (status === 'archived') return 'Archived';
  return 'Active';
}

function optionFromApi(row: {
  partner_id: string;
  name: string;
  status: WorkspaceStatus;
}): WorkspaceOption | null {
  if (!isWorkspaceId(row.partner_id)) return null;
  const fallback = WORKSPACES.find((option) => option.id === row.partner_id);
  return {
    id: row.partner_id,
    name: row.name,
    status: row.status,
    tagline: fallback?.tagline ?? statusLabel(row.status),
  };
}

export function WorkspaceSwitcher({ prioritizeReview = false }: { prioritizeReview?: boolean }) {
  const dismissHeaderOverlays = useDismissHeaderOverlays();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId, setWorkspaceId } = useWorkspace();
  const boot = useBootstrap(!prioritizeReview);

  const options = useMemo(() => {
    const fromApi = boot.data?.workspaces
      .map(optionFromApi)
      .filter((option): option is WorkspaceOption => Boolean(option));
    return fromApi?.length ? fromApi : WORKSPACES;
  }, [boot.data?.workspaces]);

  return (
    <label className="flex items-center gap-2 text-xs text-[var(--muted)]">
      <span>Workspace</span>
      <select
        aria-label="Workspace"
        onFocus={dismissHeaderOverlays}
        onPointerDown={dismissHeaderOverlays}
        className="rounded-md border border-[var(--border)] bg-[var(--panel)] px-2 py-1 text-sm font-semibold text-[var(--text)]"
        value={workspaceId}
        onChange={(event) => {
          if (!isWorkspaceId(event.target.value)) return;
          const nextWorkspaceId = event.target.value;
          const rest = location.pathname.match(/^\/app\/[^/]+(\/.*)?$/)?.[1] ?? '';
          setWorkspaceId(nextWorkspaceId);
          navigate(`${workspacePath(nextWorkspaceId, rest)}${workspacePickerSearch(location.pathname, location.search)}${location.hash}`, { replace: false });
        }}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}{option.status === 'pilot' ? ' · Pilot' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}
